import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { sql } from "drizzle-orm";
import { fiscalYears } from "./fiscal-years";

export const FINANCIAL_STATEMENT_ENTITY_PROFILES = [
  "general",
  "sme",
  "public_interest",
  "group",
] as const;

export const FINANCIAL_STATEMENT_NOTE_CATEGORIES = [
  "accounting_policy",
  "asset",
  "lease",
  "related_party",
  "tax",
  "contingency",
  "commitment",
  "other",
] as const;

export const FINANCIAL_STATEMENT_NOTE_STATUSES = [
  "draft",
  "reviewed",
  "approved",
  "archived",
] as const;

export const financialStatementNotes = sqliteTable("accounting_financial_statement_notes", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  branchId: text("branch_id"),
  fiscalYearId: text("fiscal_year_id").notNull().references(() => fiscalYears.id),
  packVersion: text("pack_version").notNull(),
  entityProfile: text("entity_profile", { enum: FINANCIAL_STATEMENT_ENTITY_PROFILES }).notNull().default("general"),
  noteCode: text("note_code").notNull(),
  title: text("title").notNull(),
  category: text("category", { enum: FINANCIAL_STATEMENT_NOTE_CATEGORIES }).notNull(),
  status: text("status", { enum: FINANCIAL_STATEMENT_NOTE_STATUSES }).notNull().default("draft"),
  isRequired: integer("is_required").notNull().default(0),
  statementLineCodesJson: text("statement_line_codes_json"),
  sourceModulesJson: text("source_modules_json"),
  systemDataJson: text("system_data_json"),
  narrativeText: text("narrative_text"),
  reviewedAt: text("reviewed_at"),
  reviewedBy: text("reviewed_by"),
  approvedAt: text("approved_at"),
  approvedBy: text("approved_by"),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  companyFiscalYearIdx: index("fsn_company_fiscal_year_idx")
    .on(table.companyId, table.fiscalYearId, table.entityProfile),
  companyStatusIdx: index("fsn_company_status_idx").on(table.companyId, table.status),
  activeBranchIdx: uniqueIndex("fsn_active_branch_idx")
    .on(table.companyId, table.branchId, table.fiscalYearId, table.packVersion, table.entityProfile, table.noteCode)
    .where(sql`${table.branchId} IS NOT NULL AND ${table.deletedAt} IS NULL`),
  activeCompanyIdx: uniqueIndex("fsn_active_company_idx")
    .on(table.companyId, table.fiscalYearId, table.packVersion, table.entityProfile, table.noteCode)
    .where(sql`${table.branchId} IS NULL AND ${table.deletedAt} IS NULL`),
}));

export const financialStatementNoteVersions = sqliteTable("accounting_financial_statement_note_versions", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  noteId: text("note_id").notNull().references(() => financialStatementNotes.id),
  versionNumber: integer("version_number").notNull(),
  action: text("action").notNull(),
  snapshotJson: text("snapshot_json").notNull(),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
}, (table) => ({
  noteVersionIdx: uniqueIndex("fsnv_note_version_idx").on(table.noteId, table.versionNumber),
  companyNoteIdx: index("fsnv_company_note_idx").on(table.companyId, table.noteId),
}));

export type FinancialStatementEntityProfile = (typeof FINANCIAL_STATEMENT_ENTITY_PROFILES)[number];
export type FinancialStatementNoteCategory = (typeof FINANCIAL_STATEMENT_NOTE_CATEGORIES)[number];
export type FinancialStatementNoteStatus = (typeof FINANCIAL_STATEMENT_NOTE_STATUSES)[number];
export type FinancialStatementNote = typeof financialStatementNotes.$inferSelect;
export type NewFinancialStatementNote = typeof financialStatementNotes.$inferInsert;
export type FinancialStatementNoteVersion = typeof financialStatementNoteVersions.$inferSelect;
export type NewFinancialStatementNoteVersion = typeof financialStatementNoteVersions.$inferInsert;
